import React, { useState, useRef } from 'react'
import { styles } from './index.sass'
import { Button, message, Card, Input, Spin as AntdSpin, Modal } from 'antd'
import axios from '../../api/axios_ml'
import { cleanHtmlContent } from '../../utils/htmlCleaner'
import { sendEmail, sendTestEmail } from '../../api/mailchimp'
import RichTextEditor from '../../components/richTextEditor'

export const Rundown = () => {
  const [topic, setTopic] = useState('')
  const [contentHtml, setContentHtml] = useState('')
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedContent, setEditedContent] = useState('')
  const editorContentRef = useRef('')

  const isValidUrl = (string) => {
    try {
      new URL(string)
      return true
    } catch (_) {
      return false
    }
  }

  const handleGenerate = async (regenerate = false) => {
    if (!topic.trim()) {
      message.error('Please enter an Apeiron Blog URL')
      return
    }

    if (!isValidUrl(topic.trim())) {
      message.error('Please enter a valid URL (e.g., https://www.blog.apeiron.life/post/the-link-between-parkinson-s-disease-and-golf)')
      return
    }

    setLoading(true)
    try {
      const payload = regenerate ? { url: topic, regenerate: true } : { url: topic }
      const response = await axios.post(
        `${process.env.REACT_APP_ML_API_ENDPOINT}/api/ml/generate-rundown`,
        payload,
      )
      const cleanedHtml = cleanHtmlContent(response.content_html)

      setContentHtml(cleanedHtml)
      message.success('Rundown email generated successfully!')
    } catch (err) {
      console.error('Error generating rundown email:', err)
      message.error(
        err.message || 'Failed to generate rundown email. Please try again.',
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    try {
      const cleanedHtml = cleanHtmlContent(contentHtml)

      if (typeof window !== 'undefined' && window.ClipboardItem) {
        const clipboardItem = new window.ClipboardItem({
          'text/html': new Blob([cleanedHtml], { type: 'text/html' }),
          'text/plain': new Blob([cleanedHtml], { type: 'text/plain' })
        })

        await navigator.clipboard.write([clipboardItem])
      } else {
        navigator.clipboard.writeText(cleanedHtml)
      }
      message.success('Copied to clipboard')
    } catch (err) {
      const cleanedHtml = cleanHtmlContent(contentHtml)
      navigator.clipboard.writeText(cleanedHtml)
      message.success('Copied to clipboard')
    }
  }

  const handleRegenerate = () => {
    Modal.confirm({
      title: 'Regenerate Rundown Email',
      content: 'This will replace the current content. Continue?',
      onOk: () => handleGenerate(true)
    })
  }

  const handleEdit = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = contentHtml
    setIsEditing(true)
  }

  const handleSave = () => {
    const finalContent = editorContentRef.current || editedContent
    setContentHtml(finalContent)
    setIsEditing(false)
    message.success('Changes saved successfully!')
  }

  const handleCancel = () => {
    setEditedContent(contentHtml)
    editorContentRef.current = ''
    setIsEditing(false)
    message.info('Changes discarded')
  }

  const handleEditorChange = (html) => {
    editorContentRef.current = html
  }

  const handleSendEmail = () => {
    Modal.confirm({
      title: 'Send Email',
      content: 'Are you sure you want to send this email?',
      okText: 'Send',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const subject = 'Rundown Email'
          const response = await sendEmail({ content: contentHtml, subject: subject })
          if (response.success === true) {
            message.success('Email sent successfully!')
          } else {
            message.error('Failed to send email. Please try again.')
          }
        } catch (err) {
          console.error('Error sending email:', err)
          message.error('Failed to send email. Please try again.')
        }
      }
    })
  }

  const handleSendTestEmail = async () => {
    const subject = 'Test Rundown Email'
    const response = await sendTestEmail({ html: contentHtml, subject: subject })
    if (response.success === true) {
      message.success('Test email sent successfully!')
    } else {
      message.error('Failed to send test email. Please try again.')
    }
  }

  return (
    <div className={styles}>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-popup">
            <AntdSpin size="large" style={{ marginBottom: '16px' }} />
            <div className="loading-text">Generating content</div>
            <div className="loading-subtext">
              This will take less than 1 minute. Please wait...
            </div>
          </div>
        </div>
      )}
      <div className="container">
        <h2>Rundown Email Generator</h2>
        <div className="input-section">
          <Input
            placeholder="Enter Apeiron Blog URL (e.g., https://www.blog.apeiron.life/post/the-link-between-parkinson-s-disease-and-golf)"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            style={{ marginBottom: '16px' }}
          />
          <div className="actions">
            <Button type="primary" onClick={handleGenerate} loading={loading}>
              Generate Rundown
            </Button>
            <Button
              onClick={handleRegenerate}
              disabled={!contentHtml || loading}
            >
              Regenerate
            </Button>
            <Button onClick={handleCopy} disabled={!contentHtml}>
              Copy Content
            </Button>
            <Button onClick={handleEdit} disabled={!contentHtml}>
              Edit Content
            </Button>
            <Button onClick={handleSendTestEmail} disabled={!contentHtml}>
              Send Test Email
            </Button>
            <Button onClick={handleSendEmail} disabled={!contentHtml}>
              Send Email
            </Button>
          </div>
        </div>
        {contentHtml && (
          <Card className="content-card">
            {isEditing ? (
              <div>
                <div style={{ marginBottom: '16px', textAlign: 'right' }}>
                  <Button onClick={handleCancel} style={{ marginRight: '8px' }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSave}>
                    Save Changes
                  </Button>
                </div>
                <RichTextEditor
                  value={editedContent}
                  onChange={handleEditorChange}
                  placeholder="Edit your Rundown content here..."
                />
              </div>
            ) : (
              <div
                className="content-preview"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
